/**
 * Currency utility functions for flexible currency formatting
 */

/**
 * Format a number with a custom currency symbol
 * @param amount - The amount to format
 * @param symbol - The currency symbol to use (e.g., "$", "€", "R", "₦")
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted currency string (e.g., "$ 1,499.00")
 */
export const formatCurrency = (amount: number, symbol: string = 'R', decimals: number = 2): string => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return `${symbol} 0.00`;
  }
  
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
  
  return `${symbol} ${formatted}`;
};

/**
 * Format a number with a custom currency symbol without space
 * @param amount - The amount to format
 * @param symbol - The currency symbol to use (e.g., "$", "€", "R", "₦")
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted currency string (e.g., "$1,499.00")
 */
export const formatCurrencySimple = (amount: number, symbol: string = 'R', decimals: number = 2): string => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return `${symbol}0.00`;
  }
  
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
  
  return `${symbol}${formatted}`;
};

/**
 * Format a number as South African Rand currency
 * @param amount - The amount to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted currency string (e.g., "R 1,499.00")
 */
export const formatRand = (amount: number, decimals: number = 2): string => {
  return formatCurrency(amount, 'R', decimals);
};

/**
 * Format a number as Rand without the currency symbol (just R)
 * @param amount - The amount to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string (e.g., "R1,499.00")
 */
export const formatRandSimple = (amount: number, decimals: number = 2): string => {
  return formatCurrencySimple(amount, 'R', decimals);
};

/**
 * Format a number as Rand with thousands separator
 * @param amount - The amount to format
 * @returns Formatted string (e.g., "R1,499")
 */
export const formatRandNoDecimals = (amount: number): string => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return 'R0';
  }
  
  const formatted = new Intl.NumberFormat('en-ZA').format(amount);
  return `R${formatted}`;
};

/**
 * Format a percentage with Rand symbol
 * @param amount - The amount to format
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string (e.g., "R45.5%")
 */
export const formatRandPercentage = (amount: number, decimals: number = 1): string => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return 'R0%';
  }
  
  return `R${amount.toFixed(decimals)}%`;
};

/**
 * Get currency symbol by currency code
 * @param currencyCode - The currency code (e.g., "USD", "EUR", "ZAR")
 * @returns The currency symbol
 */
export const getCurrencySymbol = (currencyCode: string): string => {
  const symbols: { [key: string]: string } = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'CAD': 'C$',
    'AUD': 'A$',
    'ZAR': 'R',
    'NGN': '₦',
    'KES': 'KSh',
    'GHS': '₵',
    'INR': '₹',
    'JPY': '¥',
    'CHF': 'Fr'
  };
  
  return symbols[currencyCode] || '$';
};

/**
 * Format currency based on currency code
 * @param amount - The amount to format
 * @param currencyCode - The currency code (e.g., "USD", "EUR", "ZAR")
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted currency string
 */
export const formatCurrencyByCode = (amount: number, currencyCode: string, decimals: number = 2): string => {
  const symbol = getCurrencySymbol(currencyCode);
  return formatCurrency(amount, symbol, decimals);
};
